import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { FlowQueryModel } from "src/app/models/flowquerymodel";
import {
  GUIDE_END_FORM_KEY,
  GuideQuestion,
  GuideQuestionAnswer,
  GuideQuestionResponse,
  GUIDE_QUESTION_TYPE_LABELS,
  isQuestionAnswered,
  questionHasOptions,
} from "src/app/models/guide-question-form.model";
import { EventEmitterService } from "src/app/Services/event-emitter.service";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { FrontlineOpsService } from "src/app/Services/frontline-ops.service";
import { OrgContextService } from "src/app/Services/org-context.service";
import { OrganizationService } from "src/app/Services/organization.service";
import { PermissionsService } from "src/app/Services/permissions.service";
import { getLoggedInUser } from "src/app/shared/utils/plan.util";

interface OptionSummary {
  id: string;
  text: string;
  count: number;
  pct: number;
  isCorrect: boolean;
}

interface RespondentAnswer {
  responseId: string;
  name: string;
  email: string;
  initials: string;
  submittedAtUtc: string | null;
  answer: string;
  answered: boolean;
  correct: boolean | null;
  photoUrl: string;
  optionIds: string[];
}

interface QuestionSummary {
  id: string;
  text: string;
  type: GuideQuestion["type"];
  typeLabel: string;
  answered: number;
  answeredPct: number;
  options: OptionSummary[];
  number: { min: number; max: number; avg: number; unit: string; outOfRange: number } | null;
  respondents: RespondentAnswer[];
}

const ANSWER_PREVIEW_LIMIT = 10;

interface FormSummary {
  key: string;
  label: string;
  title: string;
  mode: string;
  responses: number;
  avgScore: number | null;
  maxScore: number | null;
  questions: QuestionSummary[];
}

@Component({
  selector: "app-question-responses",
  templateUrl: "./question-responses.component.html",
  styleUrls: ["../task-mode-reports/task-mode-reports.component.scss", "./question-responses.component.scss"],
})
export class QuestionResponsesComponent implements OnInit, OnChanges {
  /** When true, the parent page owns the guide selector, access check and page shell. */
  @Input() embedded = false;
  @Input() flowId = "";

  userData: any;
  flows: { id: string; title: string }[] = [];
  selectedFlowId = "";
  selectedFormKey = "";
  loadingFlows = false;
  loadingResponses = false;
  responses: GuideQuestionResponse[] = [];
  forms: FormSummary[] = [];
  expanded = new Set<string>();
  expandedForms = new Set<string>();
  openAnswers = new Set<string>();
  showAllAnswers = new Set<string>();
  optionFilter: Record<string, string> = {};
  private responsesSub?: Subscription;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private eventEmitterService: EventEmitterService,
    private flowService: FlowServiceService,
    private frontlineOps: FrontlineOpsService,
    private orgContext: OrgContextService,
    private organizationService: OrganizationService,
    private permissions: PermissionsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.embedded && changes["flowId"] && this.flowId !== this.selectedFlowId) {
      this.selectedFlowId = this.flowId || "";
      this.selectedFormKey = "";
      this.loadReport();
    }
  }

  ngOnInit(): void {
    if (this.embedded) return;
    this.userData = getLoggedInUser();
    if (!this.userData) {
      this.router.navigate(["/"]);
      return;
    }
    if (!this.canAccess()) {
      this.toastr.warning(
        "Question responses are available to Content Creators and organization / application / team admins."
      );
      this.router.navigate(["/user-dashboard/dashboard"]);
      return;
    }

    this.eventEmitterService.setDashboardMenuStyleProperty("question-responses,Question Responses");
    this.loadFlows();
  }

  canAccess(): boolean {
    const role = this.permissions.resolveEffectiveRole(this.userData);
    if (role === "Super Admin" || role === "Admin") return true;
    if (this.orgContext.isOrganizationMode()) {
      return this.orgContext.canViewUsersTab();
    }
    return role === "Content Creator";
  }

  private loadFlows(): void {
    this.loadingFlows = true;
    if (this.orgContext.isOrganizationMode()) {
      const orgId = this.orgContext.contentScope().organizationId;
      if (!orgId) {
        this.loadingFlows = false;
        this.flows = [];
        return;
      }
      this.organizationService.listAdminContent(orgId).subscribe({
        next: (rows) => {
          this.setFlows(
            (rows || [])
              .filter((r: any) => {
                const type = String(r.contentType || r.ContentType || "Guide").toLowerCase();
                return type === "guide" || type === "flow" || !type;
              })
              .map((r: any) => ({
                id: r.contentId || r.ContentId || r.rowKey || r.RowKey || "",
                title: r.title || r.Title || "Untitled guide",
              }))
          );
        },
        error: () => {
          this.loadingFlows = false;
          this.flows = [];
          this.toastr.error("Could not load organization guides");
        },
      });
      return;
    }

    const flowQueryModel = new FlowQueryModel({
      userEmail: this.userData.emailAddress,
      otherUserEmail: this.userData.emailAddress,
      pageIndex: 0,
      category: "All",
    });
    this.flowService.getAllUserFlows(flowQueryModel).subscribe({
      next: (data: any) => {
        const list = data?.flows || data?.Flows || [];
        this.setFlows(
          (Array.isArray(list) ? list : []).map((f: any) => ({
            id: f.rowKey || f.RowKey || "",
            title: f.title || f.Title || "Untitled guide",
          }))
        );
      },
      error: () => {
        this.loadingFlows = false;
        this.flows = [];
        this.toastr.error("Could not load your guides");
      },
    });
  }

  private setFlows(list: { id: string; title: string }[]): void {
    this.flows = list.filter((f) => !!f.id);
    this.loadingFlows = false;
    if (this.flows.length && !this.selectedFlowId) {
      this.selectedFlowId = this.flows[0].id;
      this.loadReport();
    }
  }

  onFlowChange(): void {
    this.selectedFormKey = "";
    this.loadReport();
  }

  loadReport(): void {
    this.responses = [];
    this.forms = [];
    this.expanded.clear();
    this.expandedForms.clear();
    this.openAnswers.clear();
    this.showAllAnswers.clear();
    this.optionFilter = {};
    if (!this.selectedFlowId) return;

    this.loadingResponses = true;
    this.responsesSub?.unsubscribe();
    this.responsesSub = this.frontlineOps.listQuestionResponses(this.selectedFlowId).subscribe({
      next: (rows) => {
        this.responses = rows || [];
        this.forms = this.buildSummaries(this.responses);
        if (this.selectedFormKey && !this.forms.some((f) => f.key === this.selectedFormKey)) {
          this.selectedFormKey = "";
        }
        this.loadingResponses = false;
      },
      error: (err) => {
        this.loadingResponses = false;
        this.toastr.error(err?.error?.error || "Could not load question responses");
      },
    });
  }

  get visibleForms(): FormSummary[] {
    return this.selectedFormKey ? this.forms.filter((f) => f.key === this.selectedFormKey) : this.forms;
  }

  get visibleResponses(): GuideQuestionResponse[] {
    return this.selectedFormKey
      ? this.responses.filter((r) => r.formKey === this.selectedFormKey)
      : this.responses;
  }

  get uniqueRespondents(): number {
    return new Set(this.visibleResponses.map((r) => (r.userEmail || r.sessionId || r.id || "").toLowerCase())).size;
  }

  get overallAvgScorePct(): number | null {
    const scored = this.visibleResponses.filter((r) => r.maxScore && r.maxScore > 0);
    if (!scored.length) return null;
    const total = scored.reduce((sum, r) => sum + (r.score || 0) / (r.maxScore as number), 0);
    return Math.round((total / scored.length) * 100);
  }

  formLabel(r: { formKey: string; stepIndex?: number | null; stepTitle?: string | null }): string {
    if (r.formKey === GUIDE_END_FORM_KEY) return "End of guide";
    const n = r.stepIndex != null ? `Step ${r.stepIndex + 1}` : "Step";
    return r.stepTitle ? `${n}: ${r.stepTitle}` : n;
  }

  toggle(r: GuideQuestionResponse): void {
    if (this.expanded.has(r.id)) this.expanded.delete(r.id);
    else this.expanded.add(r.id);
  }

  isFormOpen(f: FormSummary): boolean {
    return this.expandedForms.has(f.key);
  }

  toggleForm(f: FormSummary, event?: Event): void {
    event?.preventDefault();
    if (this.expandedForms.has(f.key)) this.expandedForms.delete(f.key);
    else this.expandedForms.add(f.key);
  }

  answersKey(f: FormSummary, q: QuestionSummary): string {
    return `${f.key}:${q.id}`;
  }

  isAnswersOpen(f: FormSummary, q: QuestionSummary): boolean {
    return this.openAnswers.has(this.answersKey(f, q));
  }

  toggleAnswers(f: FormSummary, q: QuestionSummary): void {
    const key = this.answersKey(f, q);
    if (this.openAnswers.has(key)) {
      this.openAnswers.delete(key);
      delete this.optionFilter[key];
    } else {
      this.openAnswers.add(key);
    }
  }

  /** Clicking an option bar lists the people who chose it; clicking it again clears the filter. */
  filterByOption(f: FormSummary, q: QuestionSummary, o: OptionSummary): void {
    const key = this.answersKey(f, q);
    if (this.optionFilter[key] === o.id) {
      delete this.optionFilter[key];
    } else {
      this.optionFilter[key] = o.id;
      this.openAnswers.add(key);
    }
  }

  activeOptionFilter(f: FormSummary, q: QuestionSummary): OptionSummary | undefined {
    const id = this.optionFilter[this.answersKey(f, q)];
    return id ? q.options.find((o) => o.id === id) : undefined;
  }

  clearOptionFilter(f: FormSummary, q: QuestionSummary): void {
    delete this.optionFilter[this.answersKey(f, q)];
  }

  filteredRespondents(f: FormSummary, q: QuestionSummary): RespondentAnswer[] {
    const optionId = this.optionFilter[this.answersKey(f, q)];
    return optionId ? q.respondents.filter((r) => r.optionIds.includes(optionId)) : q.respondents;
  }

  visibleRespondents(f: FormSummary, q: QuestionSummary): RespondentAnswer[] {
    const list = this.filteredRespondents(f, q);
    return this.showAllAnswers.has(this.answersKey(f, q)) ? list : list.slice(0, ANSWER_PREVIEW_LIMIT);
  }

  hiddenRespondentCount(f: FormSummary, q: QuestionSummary): number {
    if (this.showAllAnswers.has(this.answersKey(f, q))) return 0;
    return Math.max(0, this.filteredRespondents(f, q).length - ANSWER_PREVIEW_LIMIT);
  }

  showAll(f: FormSummary, q: QuestionSummary): void {
    this.showAllAnswers.add(this.answersKey(f, q));
  }

  trackByKey(_: number, item: { key?: string; id?: string; responseId?: string }): string {
    return item.key || item.responseId || item.id || "";
  }

  answerFor(r: GuideQuestionResponse, q: GuideQuestion): GuideQuestionAnswer | undefined {
    return (r.answers || []).find((a) => a.questionId === q.id);
  }

  answerText(r: GuideQuestionResponse, q: GuideQuestion): string {
    const a = this.answerFor(r, q);
    if (!a) return "No answer";
    if (q.type === "longText" || q.type === "text") return a.text || "No answer";
    if (q.type === "number") {
      const value = a.numberValue ?? (a as any).NumberValue;
      if (value === null || value === undefined || value === "") return "No answer";
      return q.unit ? `${value} ${q.unit}` : String(value);
    }
    if (q.type === "photo") return a.photoUrl || (a as any).PhotoUrl || "No photo";
    if (q.type === "confirmation") return a.confirmed || (a as any).Confirmed ? "Confirmed" : "Not confirmed";
    const chosen = (q.options || []).filter((o) => (a.optionIds || []).includes(o.id)).map((o) => o.text);
    return chosen.length ? chosen.join(", ") : "No answer";
  }

  /** null when the question isn't checked (feedback without an accepted answer, long answer, no correct option). */
  answerCorrect(r: GuideQuestionResponse, q: GuideQuestion): boolean | null {
    const result = (r.fieldResults || []).find((x) => (x.questionId || (x as any).QuestionId) === q.id);
    const hasRule =
      r.mode === "score" ||
      !!q.acceptedOptionId ||
      (q.type === "number" && (q.minValue != null || q.maxValue != null));
    if (result && hasRule) return (result.passed ?? (result as any).Passed) !== false;
    if (r.mode !== "score" || q.type === "longText") return null;
    const correct = (q.options || []).filter((o) => o.isCorrect).map((o) => o.id);
    if (!correct.length) return null;
    const chosen = this.answerFor(r, q)?.optionIds || [];
    return chosen.length === correct.length && correct.every((id) => chosen.includes(id));
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return "—";
    const d = new Date(value);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  }

  openGuide(): void {
    if (!this.selectedFlowId) return;
    const flow = this.flows.find((f) => f.id === this.selectedFlowId);
    const title = (flow?.title || "guide").replace(/\s+/g, "-");
    this.router.navigate(["/guide", title, this.selectedFlowId], { queryParams: { full: 1 } });
  }

  /** Uses the question copy stored with the response so older answers keep their own option text. */
  private toRespondent(r: GuideQuestionResponse, latest: GuideQuestion): RespondentAnswer {
    const q = (r.questions || []).find((x) => x.id === latest.id) || latest;
    const a = this.answerFor(r, q);
    const name = (r.userName || "").trim() || (r.userEmail || "").split("@")[0] || "Anonymous";
    const initials = name
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "?";
    const photoUrl = q.type === "photo" ? a?.photoUrl || (a as any)?.PhotoUrl || "" : "";
    return {
      responseId: r.id,
      name,
      email: r.userEmail || "",
      initials,
      submittedAtUtc: r.submittedAtUtc || null,
      answer: q.type === "photo" ? (photoUrl ? "Photo attached" : "No photo") : this.answerText(r, q),
      answered: isQuestionAnswered(q, a),
      correct: this.answerCorrect(r, q),
      photoUrl,
      optionIds: a?.optionIds || [],
    };
  }

  /** Groups responses by form and counts answers against the newest stored copy of each form's questions. */
  private buildSummaries(rows: GuideQuestionResponse[]): FormSummary[] {
    const byKey = new Map<string, GuideQuestionResponse[]>();
    rows.forEach((r) => {
      const list = byKey.get(r.formKey) || [];
      list.push(r);
      byKey.set(r.formKey, list);
    });

    const summaries: FormSummary[] = [];
    byKey.forEach((list, key) => {
      const latest = list.reduce((a, b) =>
        new Date(b.submittedAtUtc || 0).getTime() > new Date(a.submittedAtUtc || 0).getTime() ? b : a
      );
      const scored = list.filter((r) => r.maxScore != null && r.maxScore > 0);
      const questions: QuestionSummary[] = (latest.questions || []).map((q) => {
        const answers = list
          .map((r) => (r.answers || []).find((a) => a.questionId === q.id))
          .filter((a): a is GuideQuestionAnswer => !!a);
        const answered = answers.filter((a) => isQuestionAnswered(q, a)).length;
        const options: OptionSummary[] = questionHasOptions(q.type)
          ? (q.options || []).map((o) => {
              const count = answers.filter((a) => (a.optionIds || []).includes(o.id)).length;
              return {
                id: o.id,
                text: o.text,
                count,
                pct: answered ? Math.round((count / answered) * 100) : 0,
                isCorrect: (latest.mode === "score" && !!o.isCorrect) || q.acceptedOptionId === o.id,
              };
            })
          : [];

        let number: QuestionSummary["number"] = null;
        if (q.type === "number") {
          const values = answers
            .map((a) => a.numberValue)
            .filter((v): v is number => v !== null && v !== undefined && !Number.isNaN(Number(v)))
            .map(Number);
          if (values.length) {
            number = {
              min: Math.min(...values),
              max: Math.max(...values),
              avg: values.reduce((s, v) => s + v, 0) / values.length,
              unit: q.unit || "",
              outOfRange: values.filter(
                (v) => (q.minValue != null && v < q.minValue) || (q.maxValue != null && v > q.maxValue)
              ).length,
            };
          }
        }

        const respondents: RespondentAnswer[] = list
          .map((r) => this.toRespondent(r, q))
          .sort((a, b) => new Date(b.submittedAtUtc || 0).getTime() - new Date(a.submittedAtUtc || 0).getTime());

        return {
          id: q.id,
          text: q.text,
          type: q.type,
          typeLabel: GUIDE_QUESTION_TYPE_LABELS[q.type] || "Question",
          answered,
          answeredPct: list.length ? Math.round((answered / list.length) * 100) : 0,
          options,
          number,
          respondents,
        };
      });

      summaries.push({
        key,
        label: this.formLabel(latest),
        title: latest.formTitle || "",
        mode: latest.mode,
        responses: list.length,
        avgScore: scored.length ? scored.reduce((s, r) => s + (r.score || 0), 0) / scored.length : null,
        maxScore: scored.length ? latest.maxScore ?? null : null,
        questions,
      });
    });

    return summaries.sort((a, b) => {
      if (a.key === GUIDE_END_FORM_KEY) return 1;
      if (b.key === GUIDE_END_FORM_KEY) return -1;
      const ai = byKey.get(a.key)?.[0]?.stepIndex ?? 0;
      const bi = byKey.get(b.key)?.[0]?.stepIndex ?? 0;
      return ai - bi;
    });
  }
}
